import { useState, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faChartLine,
  faRobot,
  faChevronLeft,
  faChevronRight,
  faBars,
  faSignOutAlt,
  faEnvelope,
  faChalkboard,
  faChevronDown,
  faFolderPlus,
  faImages,
  faChartPie,
  faUsers,
  faClipboardList,
  faHandshake,
  faBell,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '@/hooks/useAuth';
import { useAuthStore } from '@/stores';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useGenerationTracker } from '@/hooks/useGenerationTracker';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { ROUTES, isFullBleedRoute } from '@/constants/routes';
import styles from './DashboardLayout.module.css';

interface NavItem {
  label: string;
  path: string;
  icon: IconDefinition;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const roleLabels: Record<string, string> = {
  admin: 'Admin',
  staff: 'Staff',
  provider: 'Provider',
  unassigned: 'Pending',
};

export const DashboardLayout = () => {
  const { user, logOut } = useAuth();
  const { getEffectiveRole, isAdmin, isStaffOrAbove, isProviderUser } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  useGenerationTracker();
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [headerSlot, setHeaderSlot] = useState<ReactNode>(null);
  const [headerTitle, setHeaderTitle] = useState<ReactNode>(null);

  const isChat = location.pathname === ROUTES.dashboardChat || location.pathname.startsWith('/dashboard/chat/');
  const isFullBleed = isFullBleedRoute(location.pathname);

  const isSectionOpen = (title: string) => {
    return openSections[title] !== false; // true by default
  };

  const toggleSection = (title: string) => {
    setOpenSections((prev) => ({ ...prev, [title]: !isSectionOpen(title) }));
  };

  useEffect(() => {
    setIsSidebarOpen(!isMobile);
  }, [isMobile]);

  const staffNavSections: NavSection[] = [
    {
      title: 'Tools',
      items: [
        { label: 'Quoting Chat', path: ROUTES.dashboardChat, icon: faRobot },
        { label: 'Add Supplier', path: ROUTES.dashboardProvidersNew, icon: faFolderPlus },
        { label: 'Deals', path: ROUTES.dashboardDeals, icon: faHandshake },
      ],
    },
    {
      title: 'Data',
      items: [
        { label: 'Suppliers', path: ROUTES.dashboardProviders, icon: faBuilding },
        { label: 'Invitations', path: ROUTES.dashboardInvitations, icon: faEnvelope },
        { label: 'Renders', path: ROUTES.dashboardRenders, icon: faImages },
        { label: 'Presentations', path: ROUTES.dashboardPresentations, icon: faChalkboard },
      ],
    },
    {
      title: 'Monitoring',
      items: [
        { label: 'Token Costs', path: ROUTES.dashboardTokenUsage, icon: faChartLine },
        { label: 'Supplier Stats', path: ROUTES.dashboardProviderStats, icon: faChartPie },
      ],
    },
    {
      title: 'Management',
      items: [{ label: 'Users', path: ROUTES.dashboardUsers, icon: faUsers }],
    },
  ];

  const adminNavSection: NavSection = {
    title: 'Admin',
    items: [
      { label: 'Admin Actions', path: ROUTES.dashboardAdminActions, icon: faClipboardList },
      { label: 'Notifications', path: ROUTES.dashboardNotifications, icon: faBell },
    ],
  };

  const providerNavSection: NavSection = {
    title: 'My Suppliers',
    items: [{ label: 'My Suppliers', path: ROUTES.dashboardProviders, icon: faBuilding }],
  };

  const visibleSections: NavSection[] = isStaffOrAbove()
    ? [...staffNavSections, ...(isAdmin() ? [adminNavSection] : [])]
    : isProviderUser()
      ? [providerNavSection]
      : []; // unassigned: no nav items

  const allNavItems = visibleSections.flatMap((section) => section.items);

  const outletContext = useMemo(
    () => ({ setHeaderSlot, setHeaderTitle }),
    [setHeaderSlot, setHeaderTitle],
  );

  const handleLogout = async () => {
    await logOut();
    navigate(ROUTES.home);
  };

  const isActive = (path: string) => {
    if (!location.pathname.startsWith(path)) return false;

    // Check if there's a more specific match in the nav items
    const hasMoreSpecificMatch = allNavItems.some(
      (item) => item.path.length > path.length && location.pathname.startsWith(item.path),
    );

    return !hasMoreSpecificMatch;
  };

  const closeMobileSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  return (
    <div className={styles.dashboardContainer}>
      {/* Overlay for mobile */}
      {isMobile && isSidebarOpen && (
        <div className={styles.overlay} onClick={closeMobileSidebar}></div>
      )}

      {/* Sidebar */}
      <aside
        className={`${styles.sidebar} ${isSidebarOpen ? styles.sidebarOpen : styles.sidebarClosed}`}
      >
        <div
          className={`${styles.sidebarHeader} ${!isSidebarOpen ? styles.sidebarHeaderClosed : ''}`}
        >
          <div
            className={`${styles.logoSection} ${isSidebarOpen ? styles.logoVisible : styles.logoHidden}`}
          >
            <img src="/logobig.png" alt="Ebombo" className={styles.logo} />
          </div>
          <button
            className={`${styles.toggleButton} ${!isSidebarOpen ? styles.toggleButtonCentered : ''}`}
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label="Toggle sidebar"
          >
            <FontAwesomeIcon icon={isSidebarOpen ? faChevronLeft : faChevronRight} />
          </button>
        </div>

        <nav className={styles.nav}>
          {visibleSections.map((section, idx) => (
            <div key={idx} className={styles.navSection}>
              {isSidebarOpen ? (
                <div
                  className={`${styles.navSectionHeader} ${styles.navSectionHeaderInteractive}`}
                  onClick={() => toggleSection(section.title)}
                >
                  <span className={styles.navSectionTitleText}>{section.title}</span>
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`${styles.sectionToggleIcon} ${isSectionOpen(section.title) ? styles.iconOpen : styles.iconClosed}`}
                  />
                </div>
              ) : (
                idx > 0 && <div className={styles.navSectionDivider} />
              )}

              <div
                className={`${styles.navItems} ${!isSidebarOpen || isSectionOpen(section.title) ? styles.navItemsOpen : styles.navItemsClosed}`}
              >
                {section.items.map((item) => (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      closeMobileSidebar();
                    }}
                    className={`${styles.navItem} ${isActive(item.path) ? styles.navItemActive : ''}`}
                    title={!isSidebarOpen ? item.label : undefined}
                  >
                    <FontAwesomeIcon icon={item.icon} className={styles.navIcon} />
                    {isSidebarOpen && <span className={styles.navLabel}>{item.label}</span>}
                    {isActive(item.path) && <div className={styles.activeIndicator}></div>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className={styles.sidebarFooter}>
          <div
            className={`${styles.userCard} ${!isSidebarOpen ? styles.userCardCollapsed : ''}`}
            onClick={() => navigate(ROUTES.profile)}
            title="View Profile"
          >
            <div className={styles.userAvatar}>
              <span>{user?.email?.charAt(0).toUpperCase()}</span>
            </div>
            {isSidebarOpen && (
              <div className={styles.userInfo}>
                <div className={styles.userName}>{user?.email}</div>
                <div className={styles.userRole}>{roleLabels[getEffectiveRole()] ?? 'User'}</div>
              </div>
            )}
          </div>
          <button onClick={handleLogout} className={styles.logoutButton} title="Log Out">
            <FontAwesomeIcon icon={faSignOutAlt} />
            {isSidebarOpen && <span>Log Out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`${styles.mainContent} ${isSidebarOpen ? styles.mainContentShifted : ''}`}>
        <header className={styles.header}>
          <button
            className={styles.mobileMenuButton}
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          >
            <FontAwesomeIcon icon={faBars} />
          </button>
          {/* Title section - custom node OR fallback to nav label */}
          <div className={styles.headerTitleContainer}>
            {!(isChat && isMobile && headerSlot) && (
              headerTitle
                ? headerTitle
                : <h1 className={styles.headerTitle}>
                    {allNavItems.find((item) => isActive(item.path))?.label || 'Ebombo'}
                  </h1>
            )}
          </div>

          {/* Page-specific header actions injected by child routes */}
          {headerSlot && <div className={styles.headerSlot}>{headerSlot}</div>}
        </header>

        <div className={isFullBleed ? styles.contentAreaChat : styles.contentArea}>
          <Outlet context={outletContext} />
        </div>
      </main>
    </div>
  );
};
